import React, {useEffect, useMemo, useState} from 'react';
import ReactApexChart from "react-apexcharts";
import {useAppDispatch, useAppSelector} from "../../../redux/store";
import {
    loadingCharts,
    selectCommerceData,
    selectCommerceDataByMediaType,
    selectCommerceOts,
    selectCommerceRegion,
    selectCommerceSpm,
    selectCommerceTime,
    selectCommerceType,
    selectCommerceChartMode,
    selectCommerceLoadingFilters
} from "../../../common/selectors";
import {
    ALL_REGIONS_VALUE,
    fetchDataCommercePercentage,
    fetchDataCommercePercentageForMediaType
} from "../../../redux/commerceReducer";
import {formatDateForCategories, getPeriod, periodLineChart, valueOf} from "../../../common/optionsForDate";
import {weekAnnotations} from "../../../common/optionsForCharts/weekAnnotations";
import {offsetXLabels} from "../../../common/optionsForCharts/visualSettings";
import {formatGrade} from "../../../common/optionsForCategories/table";
import {DELTA, FONT_SIZE_DATA_LABELS, HEIGHT} from "../../../common/constants";
import {lightColor, textColor} from "../../../common/optionsForColors";
import {commerceOptions, commerceOrderData} from "../../../common/optionsForCheckBoxes";
import {EmptyComponent} from "../../../common/empty";
import {Spinner} from "../../../common/spin";
import styles from './commerceContetnt.module.sass';
import dayjs from "dayjs";
import {CommerceDataType} from "../../../common/types";

const OTS_SUFFIX = ' OTS'

const truncPct = (v: number) => (Math.trunc(v * 10) / 10).toFixed(1)

const mediaTypeLabels: Record<string, string> = {
    'internet_video': 'Реклама Интернет',
    'outdoor_advertising': 'Реклама Наружка',
    'tv': 'Реклама ТВ',

}

const commerceTypeLabels: Record<string, string> = {
    'ecom': 'E-commerce',
    'egroc': 'E-grocery',
}

const getSpmLabel = (value: string): string => {
    const option = commerceOptions.find(opt => opt.value === value)
    return option ? option.label : value
}

const getSpmColor = (value: string): string => {
    const option = commerceOptions.find(opt => opt.value === value)
    return option ? option.color : '#808080'
}

interface CommerceChartSingleProps {
    data: CommerceDataType[]
    title: string
    sortedSelectedSpm: string[]
    ots: boolean
    time: string[]
    chartId: string
}

const CommerceChartSingle: React.FC<CommerceChartSingleProps> = ({data, title, sortedSelectedSpm, ots, time, chartId}) => {
    const [hiddenSeries, setHiddenSeries] = useState<number[]>([])

    const formTime = valueOf(time)
    const period = getPeriod(time)
    const tickAmountTime = +(period / 86400000).toFixed()
    const tickAmountWeek = +(period / 604800000).toFixed()
    const offsetXLab = offsetXLabels(period)

    const otsTransformMeta = useMemo(() => {
        let maxPercent = 0, maxOts = 0
        data.forEach((item: CommerceDataType) => {
            if (item.percent > maxPercent) maxPercent = item.percent
            if (item.ots > maxOts) maxOts = item.ots
        })
        return {maxPercent, maxOts}
    }, [data])

    const chartColors = useMemo(() => {
        const colors = sortedSelectedSpm.map(spm => getSpmColor(spm))
        return ots ? [...colors, ...colors] : colors
    }, [sortedSelectedSpm, ots])

    const seriesData = useMemo(() => {
        const groupedPercent: Record<string, Map<number, number>> = {}
        const groupedOts: Record<string, Map<number, number>> = {}
        const allTimestamps = new Set<number>()

        data.forEach((item: CommerceDataType) => {
            const spmKey = item.spm.toUpperCase()
            const timestamp = dayjs(item.week_start).valueOf() + 302400000
            allTimestamps.add(timestamp)

            if (!groupedPercent[spmKey]) groupedPercent[spmKey] = new Map()
            groupedPercent[spmKey].set(timestamp, item.percent)

            if (ots) {
                if (!groupedOts[spmKey]) groupedOts[spmKey] = new Map()
                groupedOts[spmKey].set(timestamp, item.ots)
            }
        })

        const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b)

        const mainSeries = sortedSelectedSpm
            .filter(spm => groupedPercent[spm]?.size > 0)
            .map(spm => {
                const pts: [number, number][] = sortedTimestamps.map(ts => [ts, groupedPercent[spm]?.get(ts) ?? 0])
                return {name: getSpmLabel(spm), data: pts}
            })

        if (!ots) return mainSeries

        const otsSeries = sortedSelectedSpm
            .filter(spm => groupedOts[spm]?.size > 0)
            .map(spm => {
                const pts: [number, number][] = sortedTimestamps.map(ts => [ts, groupedOts[spm]?.get(ts) ?? 0])
                return {name: getSpmLabel(spm) + OTS_SUFFIX, data: pts}
            })

        return [...mainSeries, ...otsSeries]
    }, [data, sortedSelectedSpm, ots])

    const actualChartColors = useMemo(() => {
        const groupedData: Record<string, boolean> = {}
        data.forEach((item: CommerceDataType) => {
            groupedData[item.spm.toUpperCase()] = true
        })
        const colors = sortedSelectedSpm
            .filter(spm => groupedData[spm])
            .map(spm => getSpmColor(spm))
        return ots ? [...colors, ...colors] : colors
    }, [data, sortedSelectedSpm, ots])

    const strokeDashArray = useMemo(() => {
        return seriesData.map(s => (s.name.endsWith(OTS_SUFFIX) || s.name.toLowerCase().endsWith('доставка')) ? 8 : 0)
    }, [seriesData])

    const points = useMemo(() => {
        const colors = actualChartColors.length > 0 ? actualChartColors : chartColors

        const mainValues: number[] = []
        seriesData
            .filter(s => !s.name.endsWith(OTS_SUFFIX))
            .forEach(s => s.data.forEach(([, y]: [number, number]) => mainValues.push(y)))

        const maxPercentAxis = mainValues.length > 0 ? Math.max(...mainValues) : 0
        const minPercentAxis = mainValues.length > 0 ? Math.min(...mainValues) : 0
        const {maxOts: metaMaxOts} = otsTransformMeta

        const effectiveMax = maxPercentAxis > 0 ? maxPercentAxis : (metaMaxOts > 0 ? metaMaxOts / 5 : 1)
        const axisMin = ots
            ? (minPercentAxis >= 0 ? -effectiveMax / 2 : minPercentAxis * 1.2)
            : (minPercentAxis >= 0 ? 0 : minPercentAxis * 1.1)
        const axisMax = ots ? effectiveMax * 1.2 : effectiveMax * 1.1
        const axisRange = axisMax - axisMin

        const applyAvoidance = (groups: Record<number, any[]>) => {
            if (axisRange === 0) return
            const px = axisRange / HEIGHT
            Object.values(groups).forEach((datePoints) => {
                datePoints.sort((a: any, b: any) => a.y - b.y)
                for (let i = 1; i < datePoints.length; i++) {
                    const prev = datePoints[i - 1]
                    const curr = datePoints[i]
                    const dataGapPx = (curr.y - prev.y) / px
                    const visualGap = dataGapPx + prev.label.offsetY - curr.label.offsetY
                    if (visualGap < DELTA) {
                        curr.label.offsetY = prev.label.offsetY + dataGapPx - DELTA
                    }
                }
            })
        }

        const allPoints: any[] = []
        const byDate: Record<number, any[]> = {}

        seriesData.forEach((series, seriesIndex) => {
            if (series.name.endsWith(OTS_SUFFIX)) return
            if (hiddenSeries.includes(seriesIndex)) return

            const background = colors[seriesIndex] || '#808080'
            const labelColor = [lightColor, '#FFFFFF', '#FECAD1', '#62f77a'].includes(background) ? textColor : '#F5F5F5'

            series.data.forEach(([x, y]: [number, number]) => {
                const point = {
                    x,
                    y,
                    yAxisIndex: 0,
                    marker: {size: 0},
                    label: {
                        offsetY: 0,
                        text: truncPct(y) + '%',
                        textAnchor: 'middle',
                        borderRadius: 10,
                        borderWidth: 0.2,
                        style: {
                            background,
                            color: labelColor,
                            fontSize: FONT_SIZE_DATA_LABELS,
                            fontWeight: '600',
                            padding: {left: 5, right: 5, top: 0, bottom: 2},
                        },
                    },
                }
                allPoints.push(point)
                if (!byDate[x]) byDate[x] = []
                byDate[x].push(point)
            })
        })

        applyAvoidance(byDate)

        if (ots) {
            const {maxOts} = otsTransformMeta
            const byDateOts: Record<number, any[]> = {}

            const otsAxisRange = maxOts > 0 ? maxOts * 5 : 1
            const otsPx = otsAxisRange / HEIGHT

            seriesData.forEach((series, seriesIndex) => {
                if (!series.name.endsWith(OTS_SUFFIX)) return
                if (hiddenSeries.includes(seriesIndex)) return

                const background = colors[seriesIndex] || '#808080'
                const labelColor = [lightColor, '#FFFFFF', '#FECAD1'].includes(background) ? textColor : '#F5F5F5'

                series.data.forEach(([x, y]: [number, number]) => {
                    const point = {
                        x,
                        y,
                        yAxisIndex: seriesIndex,
                        marker: {size: 0},
                        label: {
                            offsetY: 0,
                            text: (y / 1000000).toFixed(1),
                            textAnchor: 'middle',
                            borderRadius: 10,
                            borderWidth: 0.2,
                            style: {
                                background,
                                color: labelColor,
                                fontSize: FONT_SIZE_DATA_LABELS,
                                fontWeight: '600',
                                padding: {left: 5, right: 5, top: 0, bottom: 2},
                            },
                        },
                    }
                    allPoints.push(point)
                    if (!byDateOts[x]) byDateOts[x] = []
                    byDateOts[x].push(point)
                })
            })

            if (otsAxisRange > 0) {
                Object.values(byDateOts).forEach((datePoints) => {
                    datePoints.sort((a: any, b: any) => a.y - b.y)
                    for (let i = 1; i < datePoints.length; i++) {
                        const prev = datePoints[i - 1]
                        const curr = datePoints[i]
                        const dataGapPx = (curr.y - prev.y) / otsPx
                        const visualGap = dataGapPx + prev.label.offsetY - curr.label.offsetY
                        if (visualGap < DELTA) {
                            curr.label.offsetY = prev.label.offsetY + dataGapPx - DELTA
                        }
                    }
                })
            }
        }

        return allPoints
    }, [seriesData, actualChartColors, chartColors, ots, otsTransformMeta, hiddenSeries])

    const yAxisConfig = useMemo(() => {
        if (seriesData.length === 0) return [{show: false}]

        const mainValues: number[] = []
        seriesData
            .filter(s => !s.name.endsWith(OTS_SUFFIX))
            .forEach(s => s.data.forEach(([, y]) => mainValues.push(y)))

        const {maxOts} = otsTransformMeta

        const maxPercent = mainValues.length > 0 ? Math.max(...mainValues) : 0
        const minPercent = mainValues.length > 0 ? Math.min(...mainValues) : 0

        return seriesData.map(s => {
            if (s.name.endsWith(OTS_SUFFIX)) {
                return {
                    seriesName: s.name,
                    show: false,
                    min: 0,
                    max: maxOts > 0 ? maxOts * 5 : 1,
                }
            }
            const effectiveMax = maxPercent > 0 ? maxPercent : (maxOts > 0 ? maxOts / 5 : 1)
            const axisMin = ots
                ? (minPercent >= 0 ? -effectiveMax / 2 : minPercent * 1.2)
                : (minPercent >= 0 ? 0 : minPercent * 1.1)
            const axisMax = ots ? effectiveMax * 1.2 : effectiveMax * 1.1
            return {
                seriesName: s.name,
                show: false,
                min: axisMin,
                max: axisMax,
            }
        })
    }, [seriesData, ots, otsTransformMeta])

    // For the main chart we prefer the tooltip to follow the cursor (not fixed)
    // because a fixed tooltip was appearing visually compressed. For other
    // charts preserve the previous fixed-topRight behavior.
    const tooltipConfig = {
        enabled: true,
        shared: true,
        intersect: false,
        theme: 'dark',
        // Keep tooltip fixed for all charts (including the main one).
        // Disable followCursor so the fixed tooltip doesn't try to follow and compress content.
        followCursor: false,
        style: {fontSize: '14px', whiteSpace: 'normal'},
        x: {
            show: true,
            formatter: function (value: any) {
                return formatDateForCategories(value)
            }
        },
        y: {
            formatter: function(value: any, {seriesIndex, w}: any) {
                const name: string = w?.globals?.seriesNames?.[seriesIndex] ?? ''
                if (name.endsWith(OTS_SUFFIX)) {
                    return (value / 1000000).toFixed(2)
                }
                return truncPct(value) + '%'
            }
        },
        marker: {show: true},
        fixed: {
            // always use fixed tooltip (so main chart tooltip is fixed like the other linear charts)
            enabled: true,
            position: 'topRight',
            offsetX: 0,
            offsetY: 0,
        },
    }

    const options = {
        chart: {
            id: chartId,
            events: {
                legendClick: function(_chartContext: any, seriesIndex: number, _config: any) {
                    setHiddenSeries(prev => prev.includes(seriesIndex) ? prev.filter(i => i !== seriesIndex) : [...prev, seriesIndex])
                },
            },
            animations: {enabled: false},
            fontFamily: 'SB Sans Text, Helvetica, sans-serif',
            type: 'line' as 'line',
            toolbar: {
                show: false,
                offsetY: -20,
                tools: {download: false, zoomin: false, zoomout: false}
            },
            zoom: {enabled: false}
        },
        annotations: {
            xaxis: weekAnnotations(formTime, period)[0],
            points,
        },
        stroke: {
            dashArray: strokeDashArray,
            width: 4
        },
        grid: {
            borderColor: '#202020',
            padding: {left: 0, right: 0},
            xaxis: {lines: {show: false}},
            yaxis: {lines: {show: true}}
        },
        plotOptions: {
            bar: {
                columnWidth: '50%',
                dataLabels: {
                    position: 'top',
                    orientation: 'vertical' as 'vertical' | 'horizontal',
                },
            }
        },
        fill: {},
        dataLabels: {
            enabled: false,
            textAnchor: 'middle' as 'middle',
            offsetY: -5,
            offsetX: 0,
            formatter: function (val: any) {
                return formatGrade(+val)
            },
            style: {fontSize: '10px', fontWeight: 600},
            background: {enabled: true, borderRadius: 10, borderWidth: .2},
        },
        markers: {size: 2},
        colors: actualChartColors.length > 0 ? actualChartColors : chartColors,
        legend: {
            position: 'top' as 'top',
            showForSingleSeries: true,
            formatter: function(seriesName: any) {
                return seriesName
            },
            horizontalAlign: 'left' as 'left',
            showForZeroSeries: true,
            fontSize: '14px',
            labels: {colors: '#FFFFFF'},
            onItemClick: {toggleDataSeries: true},
            onItemHover: {highlightDataSeries: false},
        },
        yaxis: yAxisConfig,
        xaxis: {
            type: 'numeric' as const,
            min: periodLineChart(time)[0],
            max: periodLineChart(time)[1],
            tickAmount: window.innerWidth > 1000 ? (period > 5500000000 ? tickAmountWeek : tickAmountTime) : tickAmountWeek,
            tickPlacement: 'between',
            axisTicks: {
                show: period < 6200000000 && true,
                offsetY: 20,
                height: 10,
            },
            axisBorder: {
                show: true,
                color: '#F5F5F5',
                height: 3,
                width: '120%',
                offsetX: -100,
                offsetY: -1
            },
            labels: {
                show: true,
                formatter: function (value: any) {
                    return window.innerWidth > 1000
                        ? (period > 5500000000 ? formatDateForCategories(value) : dayjs(value).format('DD'))
                        : formatDateForCategories(value)
                },
                offsetX: offsetXLab,
                offsetY: 3,
                style: {colors: 'white', fontWeight: 14, fontSize: '12'},
            },
            tooltip: {enabled: false},
        },
        tooltip: tooltipConfig,
    }

    if (data.length === 0) {
        return (
            <div className={styles.main}>
                <div className={styles.title}>{title}</div>
                <div className={styles.divider}></div>
                <EmptyComponent/>
            </div>
        )
    }

    return (
        <div className={styles.main}>
            <div className={styles.title}>{title}</div>
            <div className={styles.divider}></div>
            <div className={styles.chart}>
                <ReactApexChart
                    options={options}
                    series={seriesData}
                    type={'line'}
                    height={'100%'}
                    style={{color: '#FFFFFF'}}
                />
            </div>
        </div>
    )
}

const mediaTypeColors: Record<string, string> = {
    'tv': '#0291ED',
    'internet_video': '#7DC35D',
    'outdoor_advertising': '#F1E812',
}

interface CommerceStackedBarChartProps {
    spm: string
    title: string
    commerceDataByMediaType: Record<string, CommerceDataType[]>
    chartId: string
    time: string[]
}

const STACK_ORDER = ['outdoor_advertising', 'internet_video', 'tv']

const CommerceStackedBarChart: React.FC<CommerceStackedBarChartProps> = ({spm, title, commerceDataByMediaType, chartId, time}) => {
    const {seriesData, categories} = useMemo(() => {
        const byWeekPerType: Record<string, Map<number, number>> = {}

        Object.keys(mediaTypeLabels).forEach(mediaType => {
            const mediaData = commerceDataByMediaType[mediaType] ?? []
            const byWeek = new Map<number, number>()
            mediaData.forEach(item => {
                const ts = dayjs(item.week_start).isoWeekday(1).valueOf() + 302400000
                if (item.spm.toUpperCase() === spm) {
                    byWeek.set(ts, item.percent)
                }
            })
            byWeekPerType[mediaType] = byWeek
        })

        // Generate all weeks in the selected range
        const startMonday = dayjs(time[0]).isoWeekday(1)
        const endSunday = dayjs(time[1]).isoWeekday(7)
        const allTimestamps: number[] = []
        let cur = startMonday
        while (!cur.isAfter(endSunday)) {
            allTimestamps.push(cur.valueOf() + 302400000)
            cur = cur.add(1, 'week')
        }

        const sortedTs = allTimestamps

        // Normalize to exactly 100% using largest-remainder method (step 0.1%)
        const normalizedPerTs: Record<number, number[]> = {}
        sortedTs.forEach(ts => {
            const rawVals = STACK_ORDER.map(mt => byWeekPerType[mt]?.get(ts) ?? 0)
            const total = rawVals.reduce((a, b) => a + b, 0)
            if (total === 0) {
                normalizedPerTs[ts] = rawVals.map(() => 0)
                return
            }
            const exact = rawVals.map(v => v / total * 100)
            const floored = exact.map(v => Math.floor(v * 10) / 10)
            const sumFloored = floored.reduce((a, b) => a + b, 0)
            const toDistribute = Math.round((100 - sumFloored) * 10)
            const rems = exact.map((v, i) => ({i, r: (v * 10) % 1}))
            rems.sort((a, b) => b.r - a.r)
            const result = [...floored]
            for (let j = 0; j < toDistribute; j++) {
                result[rems[j].i] = parseFloat((result[rems[j].i] + 0.1).toFixed(1))
            }
            normalizedPerTs[ts] = result
        })

        const categories = sortedTs.map(ts => formatDateForCategories(ts))

        const seriesData = STACK_ORDER.map((mediaType, stackIdx) => ({
            name: mediaTypeLabels[mediaType],
            data: sortedTs.map(ts => normalizedPerTs[ts]?.[stackIdx] ?? 0),
        }))

        return {seriesData, categories}
    }, [commerceDataByMediaType, spm, time])

    const hasData = seriesData.some(s => s.data.length > 0)

    const options = {
        chart: {
            id: chartId,
            animations: {enabled: false},
            fontFamily: 'SB Sans Text, Helvetica, sans-serif',
            type: 'bar' as 'bar',
            stacked: true,
            toolbar: {show: false},
            zoom: {enabled: false},
        },
        plotOptions: {
            bar: {
                columnWidth: '90%',
                dataLabels: {position: 'center'},
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function(val: number) {
                return val > 5 ? val.toFixed(1) + '%' : ''
            },
            style: {fontSize: '18px', fontWeight: 600, colors: ['#141414']},
        },
        colors: STACK_ORDER.map(mt => mediaTypeColors[mt] || '#808080'),
        legend: {
            position: 'top' as 'top',
            showForSingleSeries: true,
            horizontalAlign: 'left' as 'left',
            fontSize: '14px',
            labels: {colors: '#FFFFFF'},
            onItemClick: {toggleDataSeries: true},
            onItemHover: {highlightDataSeries: false},
        },
        grid: {
            borderColor: '#202020',
            padding: {left: 0, right: 0},
            xaxis: {lines: {show: false}},
            yaxis: {lines: {show: true}},
        },
        yaxis: {
            show: false,
            min: 0,
            max: 100,
        },
        xaxis: {
            type: 'category' as const,
            categories,
            axisBorder: {
                show: true,
                color: '#F5F5F5',
                height: 3,
            },
            axisTicks: {show: false},
            labels: {
                show: true,
                offsetY: 3,
                style: {colors: 'white', fontWeight: 14, fontSize: '12'},
            },
            tooltip: {enabled: false},
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            theme: 'dark',
            followCursor: false,
            style: {fontSize: '14px'},
            x: {show: true},
            y: {
                formatter: function(value: any) {
                    return value > 0 ? value.toFixed(1) + '%' : '0.0%'
                },
            },
            marker: {show: true},
            fixed: {
                enabled: true,
                position: 'topRight',
                offsetX: 0,
                offsetY: 0,
            },
        },
    }

    if (!hasData) {
        return (
            <div className={styles.main}>
                <div className={styles.title}>{title}</div>
                <div className={styles.divider}></div>
                <EmptyComponent/>
            </div>
        )
    }

    return (
        <div className={styles.main}>
            <div className={styles.title}>{title}</div>
            <div className={styles.divider}></div>
            <div className={styles.chartBar}>
                <ReactApexChart
                    options={options}
                    series={seriesData}
                    type={'bar'}
                    height={'100%'}
                    style={{color: '#FFFFFF'}}
                />
            </div>
        </div>
    )
}

export const CommerceContent = () => {
    const dispatch = useAppDispatch()

    const commerceType = useAppSelector(selectCommerceType)
    const selectedRegion = useAppSelector(selectCommerceRegion)
    const selectedSpm = useAppSelector(selectCommerceSpm)
    const time = useAppSelector(selectCommerceTime)
    const ots = useAppSelector(selectCommerceOts)
    const commerceData = useAppSelector(selectCommerceData)
    const commerceDataByMediaType = useAppSelector(selectCommerceDataByMediaType)
    const loading = useAppSelector(loadingCharts)
    const loadingFilters = useAppSelector(selectCommerceLoadingFilters)
    const chartMode = useAppSelector(selectCommerceChartMode)

    useEffect(() => {
        if (selectedSpm.length > 0 && selectedRegion.length > 0) {
            const region = selectedRegion.includes(ALL_REGIONS_VALUE) ? '' : selectedRegion.join(',')
            const baseParams = {
                from_date: time[0],
                to_date: time[1],
                com_type: commerceType,
                spm: selectedSpm.join(','),
                region,
            }
            const requests = [
                dispatch(fetchDataCommercePercentage({...baseParams, media_type: null})),
                ...Object.keys(mediaTypeLabels).map(mediaType =>
                    dispatch(fetchDataCommercePercentageForMediaType({...baseParams, media_type: mediaType}))
                ),
            ]
            return () => {
                requests.forEach(request => request.abort())
            }
        }
    }, [dispatch, commerceType, selectedRegion, selectedSpm, time])

    const title = useMemo(() => {
        const typeLabel = commerceTypeLabels[commerceType] || commerceType
        return `${typeLabel} / Реклама`
    }, [commerceType])

    const sortedSelectedSpm = useMemo(() => {
        return [...selectedSpm].sort((a, b) => {
            const indexA = commerceOrderData.indexOf(a)
            const indexB = commerceOrderData.indexOf(b)
            if (indexA !== -1 && indexB !== -1) return indexA - indexB
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1
            return a.localeCompare(b)
        })
    }, [selectedSpm])

    const waitingForFiltersOrSpm =
        loadingFilters ||
        (selectedSpm.length === 0 &&
            commerceData.length === 0 &&
            Object.keys(commerceDataByMediaType).length === 0)

    if (loading === 'loading' || waitingForFiltersOrSpm) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.spinnerWrapper}>
                    <Spinner/>
                </div>
            </div>
        )
    }

    if (chartMode === 'bar') {
        return (
            <div className={styles.wrapper}>
                {sortedSelectedSpm.map(spm => (
                    <CommerceStackedBarChart
                        key={spm}
                        spm={spm}
                        title={`${commerceTypeLabels[commerceType] || commerceType} / ${getSpmLabel(spm)}`}
                        commerceDataByMediaType={commerceDataByMediaType}
                        chartId={`commerce-bar-${spm}`}
                        time={time}
                    />
                ))}
            </div>
        )
    }

    return (
        <div className={styles.wrapper}>
            <CommerceChartSingle
                data={commerceData}
                title={title}
                sortedSelectedSpm={sortedSelectedSpm}
                ots={ots}
                time={time}
                chartId="commerce-chart-main"
            />
            {Object.entries(mediaTypeLabels).map(([mediaType, mediaLabel]) => (
                <CommerceChartSingle
                    key={mediaType}
                    data={commerceDataByMediaType[mediaType] ?? []}
                    title={`${commerceTypeLabels[commerceType] || commerceType} / ${mediaLabel}`}
                    sortedSelectedSpm={sortedSelectedSpm}
                    ots={ots}
                    time={time}
                    chartId={`commerce-chart-${mediaType}`}
                />
            ))}
        </div>
    )
}
